import { useState } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';
import { activeCouncilNames, councilSelectorLabel } from '../config/registry';

const COUNCILS = activeCouncilNames();

export default function CouncilSelector({ councilCount, onComplete }) {
  const { user } = useAuth();
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const toggle = (council) => {
    setSelected(prev => {
      if (prev.includes(council)) return prev.filter(c => c !== council);
      if (prev.length >= councilCount) return prev; // at capacity
      return [...prev, council];
    });
  };

  const handleConfirm = async () => {
    if (selected.length !== councilCount || saving) return;
    setSaving(true);
    setSaveError(null);
    const { error } = await supabase
      .rpc('select_pro_councils', { p_councils: selected });
    if (error) {
      console.error('[SiteScope] CouncilSelector: save failed', error.message);
      setSaveError('Could not save your selection. Please try again.');
      setSaving(false);
      return;
    }
    onComplete(selected);
  };

  const canConfirm = selected.length === councilCount && !saving;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2000,
      background: '#0a0f1a',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px',
      fontFamily: "'DM Sans','Helvetica Neue',sans-serif",
    }}>
      <div style={{ maxWidth: '420px', width: '100%' }}>

        {/* Logo mark */}
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <div style={{
            width: '56px', height: '56px', borderRadius: '14px',
            background: 'linear-gradient(135deg,#d4a84a,#d4782f)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '28px', fontWeight: '700', color: '#0a0f1a',
            fontFamily: 'Georgia, serif',
            border: '2px solid rgba(212,168,74,0.3)',
          }}>S</div>
        </div>

        {/* Heading */}
        <div style={{ textAlign: 'center', marginBottom: '6px', fontFamily: 'Georgia, serif', fontSize: '22px', color: '#e8e0d8', fontWeight: '700' }}>
          Welcome to SiteScope Pro!
        </div>
        <div style={{ textAlign: 'center', marginBottom: '28px', fontSize: '14px', color: '#9ca3af', lineHeight: 1.6 }}>
          Select your {councilCount === 1 ? 'council' : `${councilCount} councils`} to unlock Pro intelligence.<br/>
          This selection is permanent — contact{' '}
          <a href="mailto:support@sitescopeapp.com" style={{ color: '#d4a84a', textDecoration: 'none' }}>
            support@sitescopeapp.com
          </a>{' '}
          to change it.
        </div>

        {/* Council cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
          {COUNCILS.map(council => {
            const isSelected = selected.includes(council);
            const isDisabled = !isSelected && selected.length >= councilCount;
            return (
              <button
                key={council}
                onClick={() => toggle(council)}
                disabled={isDisabled}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '14px 16px', borderRadius: '10px',
                  border: isSelected ? '1.5px solid #d4a84a' : '1.5px solid #1e2538',
                  background: isSelected ? 'rgba(212,168,74,0.1)' : '#0f1629',
                  color: isDisabled ? '#4b5563' : '#e8e0d8',
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  fontSize: '15px', fontWeight: '600',
                  fontFamily: "'DM Sans',sans-serif",
                  transition: 'border-color 0.15s, background 0.15s',
                  opacity: isDisabled ? 0.5 : 1,
                }}
              >
                <span>{councilSelectorLabel(council)}</span>
                {isSelected && (
                  <span style={{ color: '#d4a84a', fontSize: '18px', lineHeight: 1 }}>✓</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Count indicator */}
        <div style={{ textAlign: 'center', marginBottom: '16px', fontSize: '13px', color: '#9ca3af' }}>
          {selected.length} of {councilCount} selected
        </div>

        {/* Error */}
        {saveError && (
          <div style={{ marginBottom: '12px', padding: '10px 14px', background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: '8px', fontSize: '13px', color: '#f87171', textAlign: 'center' }}>
            {saveError}
          </div>
        )}

        {/* Confirm button */}
        <button
          onClick={handleConfirm}
          disabled={!canConfirm}
          style={{
            width: '100%', padding: '14px', borderRadius: '10px',
            border: 'none', cursor: canConfirm ? 'pointer' : 'not-allowed',
            background: canConfirm ? '#d4a84a' : '#1e2538',
            color: canConfirm ? '#0a0f1a' : '#4b5563',
            fontSize: '15px', fontWeight: '700',
            fontFamily: "'DM Sans',sans-serif",
            transition: 'background 0.15s, color 0.15s',
          }}
        >
          {saving ? 'Saving…' : 'Confirm Selection'}
        </button>
      </div>
    </div>
  );
}
